import React, { useState, useMemo, useEffect } from 'react';
import { supabase } from '../supabaseClient'; 
import { 
   Users, Zap, Star, Search, X, 
  ShieldCheck, ChevronRight, CheckCircle2, 
  User, Phone, Calendar, Mail
} from 'lucide-react';

import HERO_BG from '../assets/eclass/image00098.jpeg';

const LuxuryCar = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All Cars');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCar, setSelectedCar] = useState(null);
  const [bookingStep, setBookingStep] = useState('details'); 
  const [formData, setFormData] = useState({ name: '', phone: '', date: '' });
 const [name, setName] = useState('');
  const categories = ["All Cars", "Sedan", "Premium Cars", "SUV AND MPV", "EV", "Coach/Bus"];

  // Background scroll prevent
  useEffect(() => {
    if (selectedCar) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [selectedCar]);

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const { data, error } = await supabase
          .from('cars')
          .select('*')
          .order('display_order', { ascending: true }); 

        if (error) throw error;
        setCars(data || []);
      } catch (error) {
        console.error('Error fetching cars:', error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

    const filteredCars = useMemo(() => {
    return cars.filter(car => {
      const matchesSearch = car.name.toLowerCase().includes(searchQuery.toLowerCase());
      if (activeCategory === 'All Cars') return matchesSearch;

      const dbCat = (car.category || "").toLowerCase().trim();
      const selected = activeCategory.toLowerCase().trim();

      const matchesCategory = 
        dbCat === selected || 
        (selected.includes("suv") && dbCat.includes("suv")) || 
        (selected.includes("premium") && dbCat.includes("premium")) ||
        (selected.includes("coach") && dbCat.includes("coach"));

      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, searchQuery, cars]);

  const closeModal = () => {
    setSelectedCar(null);
    setBookingStep('details');
    setFormData({ name: '', phone: '', date: '' });
    setName('');
  };

  if (loading) return <div className="text-center py-20 font-black tracking-widest animate-pulse">LOADING FLEET...</div>;

  return (
    <div id="fleet-section" className="min-h-screen bg-[#f0f7ff] pb-10 md:pb-20 selection:bg-blue-100 font-sans overflow-x-hidden">  
      {/* Hero Section */}
      <div 
        className="relative text-center flex items-center justify-center h-[60vh] md:h-[95vh] bg-cover bg-center overflow-hidden" 
        style={{ backgroundImage: `url(${HERO_BG})` }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a1128]/90 via-[#0a1128]/80 to-[#0a1128]/95 z-0"></div>
        
        <div className="relative z-10 px-4">
          <p className="text-blue-400 font-bold text-[10px] md:text-xs tracking-[0.3em] md:tracking-[0.4em] uppercase mb-3">Premium Mobility</p>
          <h1 className="text-3xl md:text-6xl font-black text-white uppercase tracking-tighter leading-tight">
            DEDICATED TO <br className="md:hidden"/><span className="text-blue-400 italic">COMFORT</span>
          </h1>
          <div className="max-w-xl mx-auto mt-8 md:mt-10">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input 
                type="text" 
                list="car-suggestions"
                placeholder="Search brand or model..." 
                className="w-full pl-10 pr-4 py-4 md:py-5 rounded-none bg-white/10 backdrop-blur-md border border-white/20 text-white placeholder:text-gray-300 focus:ring-2 focus:ring-blue-500 outline-none transition-all shadow-2xl text-xs md:text-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <datalist id="car-suggestions">
                {cars.map((car) => <option key={car.id} value={car.name} />)}
              </datalist>
            </div>
          </div>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="max-w-7xl mx-auto px-4 mt-8 md:mt-12">
        <div className="flex overflow-x-auto no-scrollbar md:flex-wrap md:justify-center gap-2 mb-10 pb-4 md:pb-0 scroll-smooth">
          {categories.map(cat => (
            <button key={cat} onClick={() => { setActiveCategory(cat); setSearchQuery(''); }}
              className={`whitespace-nowrap px-6 py-3 rounded-none text-[9px] md:text-[10px] font-black tracking-widest uppercase transition-all duration-300 flex-shrink-0 ${activeCategory === cat ? 'bg-blue-600 text-white shadow-lg' : 'bg-white border border-gray-100 text-slate-500'}`}>
              {cat}
            </button>
          ))}
        </div>

        {/* Car Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {filteredCars.map(car => (
            <div key={car.id} className="group bg-white border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 p-2 md:p-2">
              <div className="relative h-48 md:h-44 w-full overflow-hidden bg-gray-50 flex items-center justify-center p-4 md:p-4">
                <img src={car.image_url} alt={car.name} className="w-full h-full object-contain transition-transform duration-700 group-hover:scale-105" />
                <div className="absolute top-3 left-3 bg-white/90 backdrop-blur-md px-2 py-1 flex items-center gap-1 shadow-sm">
                  <Star size={10} className="text-yellow-500 fill-yellow-500" />
                  <span className="font-black text-[9px] text-slate-800">{car.rating || '5.0'}</span>
                </div>
              </div>
              <div className="p-4 md:p-4">
                <p className="text-blue-600 text-[9px] font-black uppercase tracking-widest mb-1">{car.category}</p>
                <h3 className="text-lg md:text-base font-bold text-slate-900 mb-3 md:mb-4 uppercase truncate">{car.name}</h3>
                <div className="flex justify-between items-center border-t border-gray-50 pt-3 md:pt-4">
                  <div className="flex items-center gap-1.5">
                    <Users size={14} className="text-slate-400" />
                    <span className="text-[8px] md:text-[9px] font-black text-slate-500 uppercase">{car.seats} SEATS</span>
                  </div>
                  <button onClick={() => setSelectedCar(car)} className="bg-slate-900 text-white px-4 py-2.5 md:px-5 md:py-2.5 hover:bg-blue-600 transition-all shadow-md flex items-center gap-2 text-[9px] md:text-[10px] font-black uppercase tracking-widest">
                    <Zap size={12} fill="currentColor"/> Book
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* MODAL SECTION */}
      {selectedCar && (
        <div className="fixed inset-0 z-[9999] flex items-end md:items-center justify-center p-0 md:p-4 overflow-hidden">
          <div className="fixed inset-0 bg-slate-900/95 backdrop-blur-md" onClick={closeModal}></div>
          <div className="relative bg-white w-full max-w-5xl rounded-t-[2.5rem] md:rounded-none flex flex-col md:flex-row h-[92vh] md:h-auto md:max-h-[90vh] shadow-2xl overflow-hidden animate-in slide-in-from-bottom duration-500">
            <button onClick={closeModal} className="absolute top-6 right-6 z-[100] bg-white/80 backdrop-blur-md p-3 rounded-full border border-gray-200 shadow-xl active:scale-95 transition-all">
              <X size={24} className="text-slate-900" />
            </button>
            
            <div className={`relative w-full md:w-1/2 h-[30%] md:h-auto flex items-center justify-center p-8 transition-colors duration-500 flex-shrink-0 ${bookingStep === 'success' ? 'bg-green-500' : 'bg-gray-50/50'}`}>
              {bookingStep !== 'success' ? (
                <>
                  <img src={selectedCar.image_url} alt={selectedCar.name} className="relative z-10 w-full h-full object-contain drop-shadow-2xl" />
                  <div className="absolute inset-0 opacity-[0.03] font-black text-6xl md:text-8xl flex items-center justify-center select-none uppercase tracking-tighter text-slate-900">
                    {selectedCar.name.split(' ')[0]}
                  </div>
                </>
              ) : (
                <div className="text-white text-center">
                  <CheckCircle2 size={60} className="mx-auto mb-4" />
                  <h2 className="text-2xl font-black uppercase tracking-tighter">Confirmed!</h2>
                </div>
              )}
            </div>

            <div className="w-full md:w-1/2 p-6 md:p-14 bg-white overflow-y-auto overflow-x-hidden border-t border-gray-100 md:border-t-0">
              <div className="pb-32 md:pb-0">
                {bookingStep === 'details' && (
                  <div className="animate-in fade-in slide-in-from-bottom-4">
                    <div className="flex items-center gap-2 mb-3 text-blue-600 font-black text-[10px] uppercase tracking-widest"><ShieldCheck size={16} /> Premium Fleet</div>
                    <h2 className="text-3xl font-black text-slate-900 mb-2 uppercase leading-none">{selectedCar.name}</h2>
                    <p className="text-slate-500 text-sm md:text-lg mb-6 italic">"{selectedCar.description || 'Professional chauffeur service for your comfort.'}"</p>
                    <div className="space-y-3 mb-8">
                      {(selectedCar.features || ["GPS Tracking", "Professional Driver", "Clean Interior"]).map((f, i) => (
                        <div key={i} className="flex items-center gap-3 text-slate-700 font-bold text-[11px] uppercase">
                          <div className="w-1.5 h-1.5 bg-blue-600" /> {f}
                        </div>
                      ))}
                    </div>
                    <button onClick={() => setBookingStep('form')} className="w-full bg-blue-600 text-white py-5 font-black text-[11px] uppercase tracking-widest flex items-center justify-center gap-2 shadow-xl shadow-blue-200 active:bg-blue-700 transition-all">
                      Reserve Now <ChevronRight size={18}/>
                    </button>
                  </div>
                )}
 {bookingStep === 'form' && (
                  <>
                    <iframe title="Map location" name="hidden_iframe_fleet" style={{ display: "none" }}></iframe>
                    <form
                      action="https://formsubmit.co/enquiry@expresstravelcorp.com"
                      method="POST"
                      target="hidden_iframe_fleet"
                      onSubmit={() => {
                        setTimeout(() => setBookingStep('success'), 1000);
                      }}
                      className="space-y-5 animate-in fade-in slide-in-from-bottom-4"
                    >
                      {/* FormSubmit Config */}
                      <input type="hidden" name="_subject" value={`Fleet Booking - ${name}`} />
                      <input type="hidden" name="_captcha" value="false" />
                      <input type="hidden" name="_template" value="table" />
                      <input type="hidden" name="car" value={selectedCar?.name} />
                      <input type="text" name="_honey" style={{ display: "none" }} />

                      <h3 className="text-xl font-black text-slate-900 uppercase">Booking Details</h3>

                      <div className="space-y-4">
                        <div className="relative">
                          <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input
                            required
                            type="text"
                            name="name"
                            placeholder="Full Name"
                            className="w-full pl-11 pr-4 py-4 bg-slate-50 border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 text-sm"
                            onChange={(e) => {
                              setName(e.target.value);
                              setFormData({ ...formData, name: e.target.value });
                            }}
                          />
                        </div>

                        <div className="relative">
                          <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input
                            required
                            type="tel"
                            name="phone"
                            placeholder="Phone Number"
                            className="w-full pl-11 pr-4 py-4 bg-slate-50 border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 text-sm"
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          />
                        </div>

                        {/* Email Input — newly added */}
                        <div className="relative">
                          <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input
                            required
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            className="w-full pl-11 pr-4 py-4 bg-slate-50 border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        </div>

                        <div className="relative">
                          <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                          <input
                            required
                            type="date"
                            name="date" max="9999-12-31"
                            className="w-full pl-11 pr-4 py-4 bg-slate-50 border-none ring-1 ring-slate-200 focus:ring-2 focus:ring-blue-500 text-sm appearance-none"
                            onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                          />
                        </div>
                      </div>

                      <div className="flex gap-3 pt-2">
                        <button type="button" onClick={() => setBookingStep('details')} className="px-6 py-4 bg-slate-100 font-bold text-slate-500 uppercase text-[10px]">Back</button>
                        <button type="submit" className="flex-1 bg-slate-900 text-white py-4 font-black text-[10px] uppercase tracking-widest">Confirm Booking</button>
                      </div>
                    </form>
                  </>
                )}

                {bookingStep === 'success' && (
                  <div className="text-center py-6">
                    <h3 className="text-2xl font-black text-slate-900 mb-3 uppercase">Thank you!</h3>
                    <p className="text-slate-500 text-sm mb-8 leading-relaxed">Our concierge will contact you at <span className="text-slate-900 font-bold">{formData.phone}</span> shortly.</p>
                    <button onClick={closeModal} className="w-full bg-slate-900 text-white py-4 font-black text-[10px] uppercase">Back to Fleet</button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LuxuryCar;